import {AlertCircleIcon, CheckIcon, Loader2Icon, SearchIcon, SparklesIcon, UploadIcon, XIcon} from "lucide-react";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";

import {Badge} from "@components/ui/badge";
import {Button} from "@components/ui/button";
import {Card, CardContent, CardHeader, CardTitle} from "@components/ui/card";
import {Input} from "@components/ui/input";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "@components/ui/select";
import {Skeleton} from "@components/ui/skeleton";
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from "@components/ui/table";
import {Tooltip, TooltipContent, TooltipProvider, TooltipTrigger} from "@components/ui/tooltip";
import {
    type AccountGroupMappingEntry,
    autoMapAccountGroups,
    bulkAssignAccountGroup,
    deleteAccountGroupMapping,
    saveAccountGroupMapping,
    saveTabConfig,
    uploadAccountGroupMappings,
    useVNextAccountGroupMappings,
    useVNextWorkspace,
} from "@hooks/use-vnext";

interface AccountMappingTabProps {
    workspaceId: string;
    scenarioId: string;
}

// ── Inline Parent Group Editor ───────────────────────────────

function ParentGroupCell({
    account,
    parentGroups,
    workspaceId,
    onSaved,
}: {
    account: AccountGroupMappingEntry;
    parentGroups: string[];
    workspaceId: string;
    onSaved: () => void;
}) {
    const [value, setValue] = useState(account.mapping?.parent_group ?? "");
    const [saving, setSaving] = useState(false);
    const isProposed = account.mapping?.status === "proposed";

    useEffect(() => {
        setValue(account.mapping?.parent_group ?? "");
    }, [account.mapping?.parent_group]);

    const handleBlur = useCallback(async () => {
        const current = account.mapping?.parent_group ?? "";
        if (value === current) return;
        setSaving(true);
        try {
            await saveAccountGroupMapping(workspaceId, account.account_id, {
                parent_group: value,
                account_group: account.mapping?.account_group,
            });
            onSaved();
        } catch {
            setValue(current);
        } finally {
            setSaving(false);
        }
    }, [value, account, workspaceId, onSaved]);

    const listId = `parent-groups-${account.account_id}`;

    return (
        <div className="relative">
            <input
                type="text"
                list={listId}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onBlur={handleBlur}
                onKeyDown={(e) => {
                    if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                }}
                placeholder="Enter parent group..."
                disabled={saving}
                className={`w-full h-7 px-2 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-[#209DB3] disabled:opacity-50 ${
                    isProposed
                        ? "border-amber-300 bg-amber-50 dark:bg-amber-950/20"
                        : "border-border bg-background"
                }`}
            />
            <datalist id={listId}>
                {parentGroups.map((g) => (
                    <option key={g} value={g} />
                ))}
            </datalist>
        </div>
    );
}

// ── Inline Account Group Editor ──────────────────────────────

function AccountGroupCell({
    account,
    accountGroups,
    workspaceId,
    onSaved,
}: {
    account: AccountGroupMappingEntry;
    accountGroups: string[];
    workspaceId: string;
    onSaved: () => void;
}) {
    const [value, setValue] = useState(account.mapping?.account_group ?? "");
    const [saving, setSaving] = useState(false);
    const isProposed = account.mapping?.status === "proposed";

    useEffect(() => {
        setValue(account.mapping?.account_group ?? "");
    }, [account.mapping?.account_group]);

    const handleBlur = useCallback(async () => {
        const current = account.mapping?.account_group ?? "";
        if (value === current) return;
        setSaving(true);
        try {
            await saveAccountGroupMapping(workspaceId, account.account_id, {
                parent_group: account.mapping?.parent_group,
                account_group: value,
            });
            onSaved();
        } catch {
            setValue(current);
        } finally {
            setSaving(false);
        }
    }, [value, account, workspaceId, onSaved]);

    const listId = `account-groups-${account.account_id}`;

    return (
        <div className="relative">
            <input
                type="text"
                list={listId}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onBlur={handleBlur}
                onKeyDown={(e) => {
                    if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                }}
                placeholder="Enter account group..."
                disabled={saving}
                className={`w-full h-7 px-2 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-[#209DB3] disabled:opacity-50 ${
                    isProposed
                        ? "border-amber-300 bg-amber-50 dark:bg-amber-950/20"
                        : "border-border bg-background"
                }`}
            />
            <datalist id={listId}>
                {accountGroups.map((g) => (
                    <option key={g} value={g} />
                ))}
            </datalist>
        </div>
    );
}

// ── Main Tab Component ─────────────────────────────────────────

export function AccountMappingTab({workspaceId}: AccountMappingTabProps) {
    const [statusFilter, setStatusFilter] = useState("all");
    const [accountTypeFilter, setAccountTypeFilter] = useState("");
    const [searchInput, setSearchInput] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [bulkParentGroup, setBulkParentGroup] = useState("");
    const [bulkAccountGroup, setBulkAccountGroup] = useState("");
    const [bulkSaving, setBulkSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadResult, setUploadResult] = useState<string | null>(null);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [autoMapping, setAutoMapping] = useState(false);
    const [autoMapResult, setAutoMapResult] = useState<string | null>(null);
    const [autoMapError, setAutoMapError] = useState<string | null>(null);
    const [approvingIds, setApprovingIds] = useState<Set<string>>(new Set());
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [guidelines, setGuidelines] = useState("");
    const [showGuidelinesPrompt, setShowGuidelinesPrompt] = useState(false);
    const guidelinesLoaded = useRef(false);

    // Load guidelines from tab config
    const {tabs} = useVNextWorkspace();
    const accountTab = useMemo(() => tabs.find((t) => t.key === "account_mapping"), [tabs]);

    useEffect(() => {
        if (accountTab && !guidelinesLoaded.current) {
            const saved = (accountTab.config as Record<string, unknown>)?.mapping_guidelines;
            if (typeof saved === "string" && saved) {
                setGuidelines(saved);
            }
            guidelinesLoaded.current = true;
        }
    }, [accountTab]);

    const persistGuidelines = useCallback((text: string) => {
        if (!accountTab) return;
        saveTabConfig(workspaceId, accountTab.id, {...(accountTab.config as Record<string, unknown>), mapping_guidelines: text});
    }, [workspaceId, accountTab]);

    // Debounce search
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(searchInput), 300);
        return () => clearTimeout(timer);
    }, [searchInput]);

    const {accounts, stats, accountGroups, parentGroups, isLoading, error, refresh} =
        useVNextAccountGroupMappings(workspaceId, statusFilter, debouncedSearch, accountTypeFilter);

    const handleRefresh = useCallback(() => {
        refresh();
    }, [refresh]);

    // Clear selection when filters change
    useEffect(() => {
        setSelected(new Set());
    }, [statusFilter, accountTypeFilter, debouncedSearch]);

    const toggleSelect = useCallback((accountId: string) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(accountId)) next.delete(accountId);
            else next.add(accountId);
            return next;
        });
    }, []);

    const toggleSelectAll = useCallback(() => {
        if (selected.size === accounts.length) {
            setSelected(new Set());
        } else {
            setSelected(new Set(accounts.map((a: AccountGroupMappingEntry) => a.account_id)));
        }
    }, [accounts, selected.size]);

    const handleBulkAssign = useCallback(async () => {
        if ((!bulkParentGroup.trim() && !bulkAccountGroup.trim()) || selected.size === 0) return;
        setBulkSaving(true);
        try {
            await bulkAssignAccountGroup(
                workspaceId,
                Array.from(selected),
                bulkParentGroup.trim(),
                bulkAccountGroup.trim(),
            );
            setSelected(new Set());
            setBulkParentGroup("");
            setBulkAccountGroup("");
            refresh();
        } finally {
            setBulkSaving(false);
        }
    }, [workspaceId, selected, bulkParentGroup, bulkAccountGroup, refresh]);

    const handleDeleteMapping = useCallback(
        async (accountId: string) => {
            try {
                await deleteAccountGroupMapping(workspaceId, accountId);
                refresh();
            } catch {
                // ignore
            }
        },
        [workspaceId, refresh],
    );

    const handleFileUpload = useCallback(
        async (e: React.ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0];
            if (!file) return;
            setUploading(true);
            setUploadResult(null);
            setUploadError(null);
            try {
                const result = await uploadAccountGroupMappings(workspaceId, file);
                setUploadResult(
                    `Imported ${result.created} new, updated ${result.updated}, skipped ${result.skipped} of ${result.total_rows} rows` +
                    (result.warnings.length ? `. ${result.warnings.length} warning(s).` : "."),
                );
                refresh();
            } catch (err) {
                setUploadError(err instanceof Error ? err.message : "Upload failed");
            } finally {
                setUploading(false);
                if (fileInputRef.current) fileInputRef.current.value = "";
            }
        },
        [workspaceId, refresh],
    );

    const handleAutoMapClick = useCallback(() => {
        setShowGuidelinesPrompt(true);
    }, []);

    const runAutoMap = useCallback(async (guidelinesText: string) => {
        setShowGuidelinesPrompt(false);
        persistGuidelines(guidelinesText);
        setAutoMapping(true);
        setAutoMapResult(null);
        setAutoMapError(null);
        try {
            const result = await autoMapAccountGroups(workspaceId, guidelinesText);
            setAutoMapResult(
                `AI proposed ${result.proposed} mapping${result.proposed !== 1 ? "s" : ""}, skipped ${result.skipped} already mapped.` +
                (result.errors?.length ? ` ${result.errors.length} error(s).` : ""),
            );
            refresh();
        } catch (err) {
            setAutoMapError(err instanceof Error ? err.message : "Auto-map failed");
        } finally {
            setAutoMapping(false);
        }
    }, [workspaceId, persistGuidelines, refresh]);

    const handleApprove = useCallback(
        async (accountId: string) => {
            setApprovingIds((prev) => new Set(prev).add(accountId));
            try {
                await saveAccountGroupMapping(workspaceId, accountId, {action: "approve"});
                refresh();
            } finally {
                setApprovingIds((prev) => {
                    const next = new Set(prev);
                    next.delete(accountId);
                    return next;
                });
            }
        },
        [workspaceId, refresh],
    );

    const handleReject = useCallback(
        async (accountId: string) => {
            setApprovingIds((prev) => new Set(prev).add(accountId));
            try {
                await saveAccountGroupMapping(workspaceId, accountId, {action: "reject"});
                refresh();
            } finally {
                setApprovingIds((prev) => {
                    const next = new Set(prev);
                    next.delete(accountId);
                    return next;
                });
            }
        },
        [workspaceId, refresh],
    );

    // Check if any selected accounts are proposed (for bulk approve)
    const selectedProposed = useMemo(() => {
        return accounts.filter(
            (a: AccountGroupMappingEntry) =>
                selected.has(a.account_id) && a.mapping?.status === "proposed",
        );
    }, [accounts, selected]);

    const handleBulkApprove = useCallback(async () => {
        if (selectedProposed.length === 0) return;
        setBulkSaving(true);
        try {
            await Promise.all(
                selectedProposed.map((a: AccountGroupMappingEntry) =>
                    saveAccountGroupMapping(workspaceId, a.account_id, {action: "approve"}),
                ),
            );
            setSelected(new Set());
            refresh();
        } finally {
            setBulkSaving(false);
        }
    }, [workspaceId, selectedProposed, refresh]);

    if (isLoading && accounts.length === 0) {
        return (
            <div className="space-y-4">
                <div className="grid gap-4 md:grid-cols-4">
                    {[1, 2, 3, 4].map((i) => (
                        <Card key={i}>
                            <CardContent className="p-4">
                                <Skeleton className="h-8 w-full" />
                            </CardContent>
                        </Card>
                    ))}
                </div>
                <Skeleton className="h-64 w-full" />
            </div>
        );
    }

    if (error) {
        return (
            <Card>
                <CardContent className="flex items-center gap-2 p-6 text-red-600">
                    <AlertCircleIcon className="size-5" />
                    <span>{error}</span>
                </CardContent>
            </Card>
        );
    }

    const mappedPct = stats.total > 0 ? Math.round((stats.mapped / stats.total) * 100) : 0;

    return (
        <div className="flex flex-col gap-4 overflow-auto pb-6">
            {/* Stats cards */}
            <div className="grid gap-4 md:grid-cols-4">
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-muted-foreground">Total Accounts</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p className="text-2xl font-bold">{stats.total}</p>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-muted-foreground">Mapped</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p className="text-2xl font-bold text-green-600">
                            {stats.mapped}
                            <span className="text-sm font-normal text-muted-foreground ml-2">({mappedPct}%)</span>
                        </p>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-muted-foreground">Proposed</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p className="text-2xl font-bold text-amber-500">{stats.proposed}</p>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-muted-foreground">Unmapped</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p className="text-2xl font-bold text-muted-foreground">{stats.unmapped}</p>
                    </CardContent>
                </Card>
            </div>

            {/* Filters row */}
            <div className="flex items-center gap-3 flex-wrap">
                <Select value={accountTypeFilter} onValueChange={setAccountTypeFilter}>
                    <SelectTrigger className="w-[160px] h-8 text-xs border-[#5BF6C8] text-[#209DB3]">
                        <SelectValue placeholder="All Types" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all_types">All Types</SelectItem>
                        <SelectItem value="EXPENSE">Expense</SelectItem>
                        <SelectItem value="DIRECTCOSTS">Direct Costs</SelectItem>
                        <SelectItem value="OVERHEADS">Overheads</SelectItem>
                    </SelectContent>
                </Select>

                <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-[130px] h-8 text-xs border-[#5BF6C8] text-[#209DB3]">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="mapped">Mapped</SelectItem>
                        <SelectItem value="proposed">Proposed</SelectItem>
                        <SelectItem value="unmapped">Unmapped</SelectItem>
                    </SelectContent>
                </Select>

                <div className="relative flex-1 max-w-xs">
                    <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground" />
                    <Input
                        placeholder="Search accounts..."
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        className="pl-8 h-8 text-xs border-[#5BF6C8]"
                    />
                </div>

                <div className="ml-auto flex items-center gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        className="h-8 text-xs text-amber-600 border-amber-400 hover:bg-amber-50 hover:text-amber-700 dark:hover:bg-amber-950/30"
                        disabled={autoMapping}
                        onClick={handleAutoMapClick}
                    >
                        {autoMapping ? (
                            <Loader2Icon className="mr-1.5 size-3.5 animate-spin" />
                        ) : (
                            <SparklesIcon className="mr-1.5 size-3.5" />
                        )}
                        {autoMapping ? "Mapping..." : "AI Auto-Map"}
                    </Button>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv,.xlsx,.xls"
                        className="hidden"
                        onChange={handleFileUpload}
                    />
                    <Button
                        variant="outline"
                        size="sm"
                        className="h-8 text-xs text-[#209DB3] border-[#209DB3] hover:bg-[#209DB3] hover:text-white"
                        disabled={uploading}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        {uploading ? (
                            <Loader2Icon className="mr-1.5 size-3.5 animate-spin" />
                        ) : (
                            <UploadIcon className="mr-1.5 size-3.5" />
                        )}
                        {uploading ? "Importing..." : "Import CSV/Excel"}
                    </Button>
                </div>
            </div>

            {/* Guidelines prompt — shown when user clicks AI Auto-Map */}
            {showGuidelinesPrompt && (
                <Card className="border-amber-300 bg-amber-50/50 dark:bg-amber-950/10">
                    <CardContent className="p-4 space-y-3">
                        <div className="flex items-center gap-2">
                            <SparklesIcon className="size-4 text-amber-500" />
                            <span className="text-sm font-medium">Mapping Guidelines</span>
                        </div>
                        <p className="text-xs text-muted-foreground">
                            Add any business-specific instructions for the AI. These will guide how accounts are grouped and categorized.
                        </p>
                        <textarea
                            value={guidelines}
                            onChange={(e) => setGuidelines(e.target.value)}
                            placeholder="e.g., Group all software under 'Technology & Software'. Separate direct labor costs from overhead payroll. Travel costs go under 'Variable OpEx'."
                            rows={3}
                            autoFocus
                            className="w-full text-xs border rounded px-2 py-1.5 bg-background focus:outline-none focus:ring-1 focus:ring-[#209DB3] resize-y placeholder:text-muted-foreground/60"
                        />
                        <div className="flex items-center gap-2 justify-end">
                            <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 text-xs"
                                onClick={() => setShowGuidelinesPrompt(false)}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 text-xs text-muted-foreground"
                                onClick={() => runAutoMap("")}
                            >
                                Skip
                            </Button>
                            <Button
                                size="sm"
                                className="h-7 text-xs bg-amber-500 hover:bg-amber-600 text-white"
                                onClick={() => runAutoMap(guidelines)}
                            >
                                <SparklesIcon className="mr-1.5 size-3" />
                                Run Auto-Map
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Feedback messages */}
            {uploadResult && <p className="text-sm text-green-600">{uploadResult}</p>}
            {uploadError && <p className="text-sm text-red-600">{uploadError}</p>}
            {autoMapResult && <p className="text-sm text-amber-600">{autoMapResult}</p>}
            {autoMapError && <p className="text-sm text-red-600">{autoMapError}</p>}

            {/* Bulk actions */}
            {selected.size > 0 && (
                <div className="flex items-center gap-3 p-2 bg-muted/50 rounded-lg border">
                    <span className="text-xs font-medium">{selected.size} selected:</span>
                    <Input
                        placeholder="Parent group..."
                        value={bulkParentGroup}
                        onChange={(e) => setBulkParentGroup(e.target.value)}
                        list="bulk-parent-groups"
                        className="h-7 w-40 text-xs"
                    />
                    <datalist id="bulk-parent-groups">
                        {parentGroups.map((g: string) => (
                            <option key={g} value={g} />
                        ))}
                    </datalist>
                    <Input
                        placeholder="Account group..."
                        value={bulkAccountGroup}
                        onChange={(e) => setBulkAccountGroup(e.target.value)}
                        list="bulk-account-groups"
                        className="h-7 w-40 text-xs"
                    />
                    <datalist id="bulk-account-groups">
                        {accountGroups.map((g: string) => (
                            <option key={g} value={g} />
                        ))}
                    </datalist>
                    <Button
                        size="sm"
                        className="h-7 text-xs bg-[#209DB3] hover:bg-[#209DB3]/90"
                        disabled={(!bulkParentGroup.trim() && !bulkAccountGroup.trim()) || bulkSaving}
                        onClick={handleBulkAssign}
                    >
                        Apply
                    </Button>
                    {selectedProposed.length > 0 && (
                        <Button
                            size="sm"
                            className="h-7 text-xs bg-green-600 hover:bg-green-700 text-white"
                            disabled={bulkSaving}
                            onClick={handleBulkApprove}
                        >
                            {bulkSaving ? (
                                <Loader2Icon className="mr-1 size-3 animate-spin" />
                            ) : (
                                <CheckIcon className="mr-1 size-3" />
                            )}
                            Approve Selected ({selectedProposed.length})
                        </Button>
                    )}
                    <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => setSelected(new Set())}
                    >
                        Clear
                    </Button>
                </div>
            )}

            {/* Table */}
            <Card>
                <CardContent className="p-0">
                    <TooltipProvider>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-10">
                                        <input
                                            type="checkbox"
                                            checked={accounts.length > 0 && selected.size === accounts.length}
                                            onChange={toggleSelectAll}
                                            className="rounded border-border"
                                        />
                                    </TableHead>
                                    <TableHead className="w-8"></TableHead>
                                    <TableHead className="text-xs w-20">Code</TableHead>
                                    <TableHead className="text-xs">Account Name</TableHead>
                                    <TableHead className="text-xs w-28">Type</TableHead>
                                    <TableHead className="text-xs w-48">Parent Group</TableHead>
                                    <TableHead className="text-xs w-48">Account Group</TableHead>
                                    <TableHead className="text-xs w-20"></TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {accounts.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                                            No accounts found
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    accounts.map((a: AccountGroupMappingEntry) => {
                                        const isMapped = a.mapping !== null && (a.mapping.parent_group || a.mapping.account_group);
                                        const isProposed = a.mapping?.status === "proposed";
                                        const isActioning = approvingIds.has(a.account_id);
                                        const typeBadge =
                                            a.account_type === "DIRECTCOSTS"
                                                ? "COGS"
                                                : a.account_type === "OVERHEADS"
                                                  ? "Overhead"
                                                  : "Expense";
                                        return (
                                            <TableRow key={a.account_id} className="group">
                                                <TableCell>
                                                    <input
                                                        type="checkbox"
                                                        checked={selected.has(a.account_id)}
                                                        onChange={() => toggleSelect(a.account_id)}
                                                        className="rounded border-border"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    {isProposed ? (
                                                        <Tooltip>
                                                            <TooltipTrigger>
                                                                <span className="inline-block w-2 h-2 rounded-full bg-amber-400" />
                                                            </TooltipTrigger>
                                                            <TooltipContent side="right" className="text-xs">
                                                                AI proposed — review and approve
                                                            </TooltipContent>
                                                        </Tooltip>
                                                    ) : (
                                                        <span
                                                            className={`inline-block w-2 h-2 rounded-full ${isMapped ? "bg-green-500" : "bg-gray-300"}`}
                                                        />
                                                    )}
                                                </TableCell>
                                                <TableCell className="text-xs font-mono text-muted-foreground">{a.account_code}</TableCell>
                                                <TableCell className="text-xs font-medium">{a.account_name}</TableCell>
                                                <TableCell>
                                                    <Badge
                                                        variant="outline"
                                                        className={`text-[10px] ${
                                                            typeBadge === "COGS"
                                                                ? "border-red-300 text-red-700"
                                                                : typeBadge === "Overhead"
                                                                  ? "border-purple-300 text-purple-700"
                                                                  : "border-orange-300 text-orange-700"
                                                        }`}
                                                    >
                                                        {typeBadge}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell>
                                                    <ParentGroupCell
                                                        account={a}
                                                        parentGroups={parentGroups}
                                                        workspaceId={workspaceId}
                                                        onSaved={handleRefresh}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <AccountGroupCell
                                                        account={a}
                                                        accountGroups={accountGroups}
                                                        workspaceId={workspaceId}
                                                        onSaved={handleRefresh}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex items-center gap-1">
                                                        {isProposed && (
                                                            <>
                                                                <button
                                                                    onClick={() => handleApprove(a.account_id)}
                                                                    disabled={isActioning}
                                                                    className="text-green-600 hover:text-green-700 disabled:opacity-50"
                                                                    title="Approve"
                                                                >
                                                                    {isActioning ? (
                                                                        <Loader2Icon className="size-3.5 animate-spin" />
                                                                    ) : (
                                                                        <CheckIcon className="size-3.5" />
                                                                    )}
                                                                </button>
                                                                <button
                                                                    onClick={() => handleReject(a.account_id)}
                                                                    disabled={isActioning}
                                                                    className="text-red-500 hover:text-red-600 disabled:opacity-50"
                                                                    title="Reject"
                                                                >
                                                                    <XIcon className="size-3.5" />
                                                                </button>
                                                            </>
                                                        )}
                                                        {isMapped && !isProposed && (
                                                            <button
                                                                onClick={() => handleDeleteMapping(a.account_id)}
                                                                className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-red-500 transition-opacity"
                                                                title="Remove mapping"
                                                            >
                                                                <XIcon className="size-3.5" />
                                                            </button>
                                                        )}
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </TooltipProvider>
                </CardContent>
            </Card>
        </div>
    );
}
